import { deepLinkingPlugin } from "@/plugins/deep-linking";
/**
 * THE APP, AS DATA (client half).
 *
 * This file is the whole client-side answer to "what does this product mount?".
 * Adding a kernel feature is one line in `appComposition`; removing it is
 * deleting that line.
 *
 * HabitStreak does NOT compose the activity feature. It has no `activity` table,
 * no endpoint serving its history and no sync rule mirroring it. The absence is
 * declared here and in `infra/fleet/registry/repos.ts`.
 */
import type { MessageDescriptor } from "@lingui/core";
import type { Accessor, Component } from "solid-js";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { PowerSyncDrizzleDB } from "@habits/powersync";
import { composition, type Composition, type PlatformTarget, plugged } from "@fleet/kernel";
import { browserDelivery } from "@fleet/export";
import { exportPlugin, tauriDelivery } from "@fleet/plugins/export";
import { notificationsPlugin } from "@fleet/plugins/notifications";
import { adminPlugin } from "@fleet/plugins/admin";
import { clearAllScheduledReminders } from "@/lib/notifications/service";
import { adminRoute } from "@/routes/_app/admin";
import { adminIndexRoute } from "@/routes/_app/admin.index";
import { adminUsersRoute } from "@/routes/_app/admin.users";
import { adminAuditLogRoute } from "@/routes/_app/admin.audit-log";
import { adminIntegrationsRoute } from "@/routes/_app/admin.integrations";
import { adminFlagsRoute } from "@/routes/_app/admin.flags";
import { adminMonetizationRoute } from "@/routes/_app/admin.monetization";
import { adminConfigRoute } from "@/routes/_app/admin.config";
import { adminMetricsRoute } from "@/routes/_app/admin.metrics";
import { adminStorageRoute } from "@/routes/_app/admin.storage";
import { adminThemeAuditRoute } from "@/routes/_app/admin.theme-audit";
import { adminThemesRoute } from "@/routes/_app/admin.themes";
import { adminFontsRoute } from "@/routes/_app/admin.fonts";
import { adminEmailRoute } from "@/routes/_app/admin.email";
import { adminEmailCampaignsRoute } from "@/routes/_app/admin.email.campaigns";
import type { Command, KeyBinding } from "@fleet/shortcuts";

interface AppNavItem {
  id: string;
  to: string;
  order: number;
  label: MessageDescriptor;
  icon: Component<{ class?: string }>;
}

export type AppContribution =
  | { kind: "nav"; item: AppNavItem }
  | { kind: "command"; command: Command; binding?: KeyBinding };

export interface HostServices {
  translate: (message: MessageDescriptor) => string;
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  onHistoryError: () => void;
}

const adminRoutes = [
  adminRoute.addChildren([
    adminIndexRoute,
    adminUsersRoute,
    adminAuditLogRoute,
    adminIntegrationsRoute,
    adminFlagsRoute,
    adminMonetizationRoute,
    adminConfigRoute,
    adminMetricsRoute,
    adminStorageRoute,
    adminThemeAuditRoute,
    adminThemesRoute,
    adminFontsRoute,
    adminEmailRoute,
    adminEmailCampaignsRoute,
  ]),
] as const;

export const pluginRoutes = [...adminRoutes] as const;

export const NO_HOST: HostServices = {
  translate: () => {
    throw new Error("composition read without a host: routes pass only");
  },
  db: () => null,
  drizzle: () => null,
  userId: () => undefined,
  onHistoryError: () => {
    throw new Error("composition read without a host: routes pass only");
  },
};

export function appComposition(
  host: HostServices,
  target: PlatformTarget,
): Composition<AppContribution> {
  void host;
  return composition<AppContribution>({ name: "habits", target }, [
    // Feature #10: scheduled notifications as a plugin. Cancels OS reminders on unmount.
    plugged(notificationsPlugin, { onTeardown: clearAllScheduledReminders }),
    // Feature #8: admin routes and contributions.
    plugged(adminPlugin, undefined, adminRoutes),
    plugged(exportPlugin, { browser: browserDelivery, fileSystem: tauriDelivery }),
    plugged(deepLinkingPlugin, { translate: host.translate }),
  ]);
}
